import React, { useState, useEffect } from 'react';
import GeneralModal from '../../../components/GeneralModal/GeneralModal';
import { useLocales, useGetTranslationKeyDetails } from '../../../hooks/useLanguages';
import { useTranslation } from '../../../hooks/useTranslation';

const LabelEditModal = ({ 
  showModal, 
  onClose, 
  onSubmit, 
  editingLabel, 
  formData, 
  onInputChange, 
  onTranslationChange, 
  getTranslationValue,
  keyTypesOptions,
  selectedLocale,
}) => {
  const {t} = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const { data: locales, isLoading: localesLoading } = useLocales({});
  const { 
    data: translationKeyDetails, 
    isLoading: detailsLoading, 
    error: detailsError 
  } = useGetTranslationKeyDetails({id: editingLabel?.id});

  // Initialize form data when translation key details are loaded
  useEffect(() => {
    if (translationKeyDetails && editingLabel) {
      // Update form data with the detailed translation information
      const updatedFormData = {
        key: translationKeyDetails.key,
        key_type_id: translationKeyDetails.key_type_id,
        translations: translationKeyDetails.translations || []
      };
      
      // Update the parent component's form data
      onInputChange('key', updatedFormData.key);
      onInputChange('key_type_id', updatedFormData.key_type_id);
      
      // Initialize translations for each locale
      if (updatedFormData.translations.length > 0) {
        updatedFormData.translations.forEach(translation => {
          onTranslationChange(translation.locale_id, translation.label);
        });
      }
    }
  }, [translationKeyDetails, editingLabel]);

  // Enhanced getTranslationValue function that works with API data
  const getEnhancedTranslationValue = (localeId) => {
    // First try to get from the current form state (user input takes priority)
    const formTranslation = getTranslationValue(localeId);
    if (formTranslation) {
      return formTranslation;
    }
    
    // Fallback to the detailed API response if no form data exists
    if (translationKeyDetails?.translations) {
      const apiTranslation = translationKeyDetails.translations.find(t => t.locale_id === localeId);
      if (apiTranslation) {
        return apiTranslation.label;
      }
    }
    
    return '';
  };

  const getEnhancedTranslationEditable = (localeId) => {
    if (translationKeyDetails?.translations) {
      const apiTranslation = translationKeyDetails.translations.find(t => t.locale_id === localeId);
      if (apiTranslation) {
        return !apiTranslation.editable;
      }
    }
    return false;
  };

    
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  // Show loading state while fetching data
  if (editingLabel && detailsLoading) {
    return (
      <GeneralModal
        title={t('labelEditModal.title.edit')}
        onClose={onClose}
        submitTitle={t('labelEditModal.submitTitle.loading')}
        cancelTitle={t('labelEditModal.cancelTitle')}
        disabled={true}
      >
        <div className="language-management__modal-content">
          <div className="language-management__loading">
            <p>{t('labelEditModal.loadingDetails')}</p>
          </div>
        </div>
      </GeneralModal>
    );
  }

  // Show error state if there's an error loading details
  if (editingLabel && detailsError) {
    return (
      <GeneralModal
        title={t('labelEditModal.title.edit')}
        onClose={onClose}
        submitTitle={t('labelEditModal.submitTitle.error')}
        cancelTitle={t('labelEditModal.cancelTitle')}
        disabled={true}
      >
        <div className="language-management__modal-content">
          <div className="language-management__error">
            <p>{t('labelEditModal.errorLoadingDetails')}</p>
          </div>
        </div>
      </GeneralModal>
    );
  }

  return (
    <GeneralModal
      title={editingLabel ? t('labelEditModal.title.edit') : t('labelEditModal.title.add')}
      onClose={onClose}
      onSubmit={handleSubmit}
      submitTitle={editingLabel ? t('labelEditModal.submitTitle.update') : t('labelEditModal.submitTitle.add')}
      cancelTitle={t('labelEditModal.cancelTitle')}
    >
      <div className="language-management__modal-content">
        <div className="language-management__form-group">
          <label>{t('labelEditModal.labelKey')}</label>
          <input
            type="text"
            value={formData.key || ''}
            onChange={(e) => onInputChange('key', e.target.value)}
            placeholder={t('labelEditModal.labelKeyPlaceholder')}
            required
            disabled
          />
        </div>
        
        <div className="language-management__form-group">
          <label>{t('labelEditModal.keyType')}</label>
          <select
            value={formData.key_type_id || ''}
            onChange={(e) => onInputChange('key_type_id', parseInt(e.target.value))}
            required
            disabled
          >
            <option value="">{t('labelEditModal.selectType')}</option>   
            {keyTypesOptions?.map((category, index) => (
              <option key={category.value} value={category.value}>{category.label}</option>
            ))}
          </select>
        </div>
        
        {/* Translation Tabs */}
        <div className="language-management__translations-section">
          <h4>{t('labelEditModal.translations')}</h4>
          {localesLoading ? (
            <div className="language-management__loading">
              <p>{t('labelEditModal.loadingLocales')}</p>
            </div>
          ) : (
            <>
              <div className="language-management__tabs">
                {locales?.map((locale, index) => {
                  const localeName = typeof locale.name === 'string' ? locale.name : (locale.name?.name || locale.name || '');
                  const localeCode = typeof locale.code === 'string' ? locale.code : (locale.code?.code || locale.code || '');
                  return (
                    <button
                      key={locale.id}
                      type="button"
                      className={`language-management__tab ${activeTab === index ? 'active' : ''}`}
                      onClick={() => setActiveTab(index)}
                    >
                      {localeName} ({localeCode})
                    </button>
                  );
                })}
              </div>
              
              <div className="language-management__tab-content">
                {locales?.map((locale, index) => {
                  if (activeTab !== index) return null;
                  const localeName = typeof locale.name === 'string' ? locale.name : (locale.name?.name || locale.name || '');
                  return (
                    <div key={locale.id} className="language-management__translation-form">
                      <div className="language-management__form-group">
                        <label>{localeName} {t('labelEditModal.translations')}</label>
                        <textarea
                          value={getEnhancedTranslationValue(locale.id)}
                          onChange={(e) => onTranslationChange(locale.id, e.target.value)}
                          placeholder={`${t('labelEditModal.translationPlaceholder')} ${localeName}`}
                          rows="3"
                          disabled={detailsLoading || getEnhancedTranslationEditable(locale.id)}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </GeneralModal>
  );
};

export default LabelEditModal;
